import React, { useState, useEffect, useCallback } from "react";

import functionsService from "../../services/functionsService";
import { auth } from "../../firebase";

import { ChevronDown, UserRoundX, MoreVertical } from "lucide-react";

import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  DialogPopover,
  DialogPopoverTrigger,
  DialogPopoverContent,
} from "../../components/ui/dialog-popover";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

import { CommonDialog } from "../../components/CommonDialog";
import { useToast } from "../../components/hooks/use-toast";
import { cn } from "../../lib/utils";

const UsersList = ({ role }) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [openPopoverId, setOpenPopoverId] = useState(null);

  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);

  const { toast } = useToast();

  // Fetch the list of users from Firebase Cloud Function
  const fetchUsers = useCallback(async () => {
    try {
      const { success, users } = await functionsService.getUsersByRole(role);

      if (!success) {
        console.error(`Error fetching users with role ${role}.`);

        toast({
          variant: "destructive",
          title: `Error fetching users with role "${role}".`,
        });

        return;
      }

      setUsers(users);
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching users with role "${role}".`, error);

      toast({
        variant: "destructive",
        title: `Error fetching users with role "${role}".`,
        description: error.message,
      });

      setLoading(false);
    }
  }, [role, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId, role) => {
    try {
      const { success } = await functionsService.updateUserRole(userId, role);

      if (!success) {
        console.error("Error updating user's role.");

        toast({
          variant: "destructive",
          title: "Error updating user's role.",
        });

        return;
      }

      fetchUsers();
      setOpenPopoverId(null);
    } catch (error) {
      console.error("Error updating role:", error);

      toast({
        variant: "destructive",
        title: "Error updating user role.",
        description: error.message,
      });
    }
  };

  const resetPassword = async (userEmail) => {
    try {
      const { success } = await functionsService.resetUserPassword(userEmail);

      if (!success) {
        console.error("Error resetting user's password.");

        toast({
          variant: "destructive",
          title: "Error resetting user's password.",
        });

        return;
      }

      toast({
        title: "Password Reset email sent",
        description: `An email was successfully sent to "${userEmail}" to reset their password.`,
      });
    } catch (error) {
      console.error("Error resetting user's password:", error);

      toast({
        variant: "destructive",
        title: "Error resetting user's password.",
        description: error.message,
      });
    }
  };

  const setUserDisabled = async (user) => {
    const newDisabled = !user.disabled;

    try {
      const { success } = await functionsService.setUserDisabled(
        user.id,
        newDisabled
      );

      if (!success) {
        console.error(`Error ${newDisabled ? "disabling" : "enabling"} user.`);

        toast({
          variant: "destructive",
          title: `Error ${newDisabled ? "disabling" : "enabling"} user.`,
        });

        return;
      }

      user.disabled = newDisabled;

      toast({
        title: `${newDisabled ? "Disable" : "Enable"} User`,
        description: `Successfully ${
          newDisabled ? "disabled" : "enabled"
        } user.`,
      });
    } catch (error) {
      console.error(
        `Error ${newDisabled ? "disabling" : "enabling"} user:`,
        error
      );

      toast({
        variant: "destructive",
        title: `Error ${newDisabled ? "disabling" : "enabling"} user.`,
        description: error.message,
      });
    }
  };

  const deleteUser = async (userId) => {
    try {
      const { success } = await functionsService.deleteUser(userId);

      if (!success) {
        console.error("Error deleting user.");

        toast({
          variant: "destructive",
          title: "Error deleting user.",
        });

        return;
      }

      toast({
        title: "Delete user",
        description: "Successfully deleted user.",
      });

      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);

      toast({
        variant: "destructive",
        title: "Error deleting user:",
        description: error.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="capitalize">{role}s</CardTitle>

        <CardDescription>
          Manage all of the application's {role}s.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6 w-full overflow-y-auto mt-2 place-self-center">
        {/* Show some loading skeletons while loading */}
        {loading &&
          [...Array(3)].map((e, i) => (
            <div key={i} className="flex items-start justify-between space-x-4">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />

                <div className="space-y-1">
                  <Skeleton className="h-4 w-[100px]" />
                  <Skeleton className="h-4 w-[150px]" />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <Skeleton className="h-8 mt-2 w-[90px]" />
              </div>
            </div>
          ))}

        {/* Users */}
        {!loading && users.length > 0 ? (
          users.map((user, index) => (
            <div
              key={index}
              className="flex align-top justify-between space-x-4"
            >
              <div className="flex items-center space-x-4">
                <Avatar
                  className={cn("h-12 w-12", user.disabled ? "opacity-50" : "")}
                >
                  <AvatarFallback>
                    {user.name.split(" ")[0][0]}
                    {user.name.split(" ")[1][0]}
                  </AvatarFallback>
                </Avatar>

                <div>
                  <p
                    className={cn(
                      "text-sm font-medium leading-none",
                      user.disabled ? "opacity-50" : ""
                    )}
                  >
                    {user.name}
                  </p>

                  <p
                    className={cn(
                      "text-sm text-muted-foreground",
                      user.disabled ? "opacity-50" : ""
                    )}
                  >
                    {user.email}
                  </p>
                </div>
              </div>

              <div>
                <DialogPopover
                  open={openPopoverId === user.id}
                  onOpenChange={(open) =>
                    setOpenPopoverId(open ? user.id : null)
                  }
                >
                  <DialogPopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto capitalize"
                      disabled={user.id === auth.currentUser.uid}
                    >
                      {user.role}
                      <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DialogPopoverTrigger>

                  <DialogPopoverContent className="p-0" align="end">
                    <Command>
                      <CommandInput placeholder="Select new role..." />

                      <CommandList>
                        <CommandEmpty>No roles found.</CommandEmpty>

                        <CommandGroup>
                          <CommandItem
                            className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                            onSelect={() => {
                              handleRoleChange(user.id, "user");
                            }}
                          >
                            <p>User</p>

                            <p className="text-sm text-muted-foreground">
                              Can view and edit hazards, depending on project
                              role.
                            </p>
                          </CommandItem>

                          <CommandItem
                            className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                            onSelect={() => {
                              handleRoleChange(user.id, "manager");
                            }}
                          >
                            <p>Manager</p>

                            <p className="text-sm text-muted-foreground">
                              Can create, edit and delete projects. Can invite
                              users to collaborate on project.
                            </p>
                          </CommandItem>

                          <CommandItem
                            className="teamaspace-y-1 flex flex-col items-start px-4 py-2"
                            onSelect={() => {
                              handleRoleChange(user.id, "admin");
                            }}
                          >
                            <p>Admin</p>

                            <p className="text-sm text-muted-foreground">
                              Can create, edit and delete any projects or
                              hazards. Can invite users to join the application.
                            </p>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </DialogPopoverContent>
                </DialogPopover>

                <CommonDialog
                  open={deleteUserDialogOpen}
                  setOpen={setDeleteUserDialogOpen}
                  title={`Delete user "${deletingUser?.name}"?`}
                  description={
                    "The user will be deleted from this application. This cannot be undone. Proceed?"
                  }
                  onConfirm={() => {
                    if (deletingUser) {
                      deleteUser(deletingUser.id);
                    }
                  }}
                />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0 ml-12"
                      disabled={user.id === auth.currentUser.uid}
                    >
                      <span className="sr-only">Open menu</span>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator></DropdownMenuSeparator>

                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          resetPassword(user.email);
                        }, 200);
                      }}
                    >
                      Reset Password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator></DropdownMenuSeparator>

                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setUserDisabled(user);
                        }, 200);
                      }}
                    >
                      {user.disabled ? "Enable User" : "Disable User"}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setDeletingUser(user);
                          setDeleteUserDialogOpen(true);
                        }, 200);
                      }}
                    >
                      Delete User
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center text-sm font-semibold space-x-2 mt-6">
            <UserRoundX className="w-5 h-5" />
            <span>No users</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsersList;
